/**
 * Whether this visitor is asked about cookies before anything is recorded.
 *
 * The user's call (2026-09-30): ask first only where the law requires it, the
 * EU/EEA, the UK and Switzerland (Spain, one of the ad markets, is asked).
 * Everywhere else analytics starts on, and "Cookie settings" in the footer turns
 * it off; an answer someone already gave is kept either way.
 *
 * It fails closed: no country, an unknown one, a slow or failed lookup, or a
 * device clock set to a European time zone (a Spaniard on a US VPN) all ask.
 * The country is the one Cloudflare puts on the pricing call, which the site
 * already makes; the answer is kept a week, so it is one call per visitor.
 */

// The EU 27, the rest of the EEA, the UK and Switzerland, plus places under their
// law that geolocation reports under their own code: the EU's outermost regions
// and Åland, and Gibraltar and the Crown Dependencies (asking there costs nothing)
export const ASK_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO',
  'GB', 'CH',
  'GF', 'GP', 'MQ', 'RE', 'YT', 'MF', 'AX',
  'GI', 'JE', 'GG', 'IM',
]);

const EUROPEAN_ZONE = /^(Europe\/|Atlantic\/(Canary|Madeira|Azores|Reykjavik|Faroe)$|Africa\/Ceuta$|Arctic\/Longyearbyen$|America\/(Guadeloupe|Martinique|Cayenne|Marigot|St_Barthelemy)$|Indian\/(Reunion|Mayotte)$)/;

const KEY = 'app.consent.region';
const KEEP_MS = 7 * 24 * 60 * 60 * 1000;
const LOOKUP_MS = 3000;

export function deviceTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/** True when this visitor has to be asked first. */
export function mustAsk(country, zone = deviceTimeZone()) {
  if (EUROPEAN_ZONE.test(zone || '')) return true;
  const code = String(country || '').toUpperCase();
  // Cloudflare: XX = unknown, T1 = Tor
  if (!/^[A-Z]{2}$/.test(code) || code === 'XX' || code === 'T1') return true;
  return ASK_COUNTRIES.has(code);
}

const storage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** 'ask' | 'implied' kept from an earlier lookup this week, or null. */
export function keptRegion(now = Date.now()) {
  try {
    const kept = JSON.parse(storage()?.getItem(KEY) || 'null');
    if (!kept || now - kept.at > KEEP_MS || !['ask', 'implied'].includes(kept.region)) return null;
    return kept.region;
  } catch {
    return null;
  }
}

function keep(region, now) {
  try {
    storage()?.setItem(KEY, JSON.stringify({ region, at: now }));
  } catch {
    // no storage: asked again next visit, which fails closed
  }
  return region;
}

/**
 * 'ask' or 'implied' (analytics on until they say no). `lookupCountry` resolves
 * to a country code. A failed or slow lookup asks and is not kept, so the next
 * visit tries again.
 */
export async function findRegion(lookupCountry, { now = Date.now(), zone = deviceTimeZone(), timeoutMs = LOOKUP_MS } = {}) {
  const kept = keptRegion(now);
  if (kept) return kept;
  if (EUROPEAN_ZONE.test(zone || '')) return keep('ask', now);
  let timer;
  try {
    const country = await Promise.race([
      lookupCountry(),
      new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('slow')), timeoutMs); }),
    ]);
    return keep(mustAsk(country, zone) ? 'ask' : 'implied', now);
  } catch {
    return 'ask';
  } finally {
    clearTimeout(timer);
  }
}
